import type { AlertColor } from '@mui/material';
import { styled, Tabs } from '@mui/material';
import type { FileRejection } from 'react-dropzone';
import { ErrorCode } from 'react-dropzone';
import type { TFunction } from 'react-i18next';
import { UPLOAD_CONSTRAINT_RESOURCES } from 'src/content/Documents/utils/utils';
import { setAlert } from 'src/redux/slices/snackbar';
import type { AppDispatch } from 'src/redux/store';
import type { IDropZoneFileValidator } from 'src/types/document';
import { ALERT, RESOURCE_TYPE } from 'src/types/enum';
import { FILE_FORMAT } from 'src/utils/constants';
import { FILE_NAME_LENGTH_LIMIT } from 'src/utils/files/file';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

interface DropHandler {
  acceptedFiles: IDropZoneFileValidator[];
  fileRejections: FileRejection[];
  currentResourceType: RESOURCE_TYPE;
  dispatch: AppDispatch;
  t: TFunction<'translation', undefined>;
  toggleUploadModal?: () => void;
  unauthorized?: boolean;
}

const isValidFile = (file) =>
  !file?.name?.startsWith('~$') && !file?.name?.startsWith('.');

export const filesValidator = (
  file: IDropZoneFileValidator,
  currentResourceType: RESOURCE_TYPE,
  dispatch: AppDispatch,
  t: TFunction<'translation', undefined>
) => {
  const path: string[] = file?.path?.split('/');

  if (!isValidFile(file)) {
    return {
      message: t(T.invalidFileType),
      code: ErrorCode.FileInvalidType
    };
  }
  if (path?.length < 2) {
    if (UPLOAD_CONSTRAINT_RESOURCES.includes(currentResourceType)) {
      dispatch(
        setAlert({
          msg: t(T.workSpaceDocumentsDropError),
          type: ALERT.ERROR
        })
      );
      return {
        message: t(T.workSpaceDocumentsDropError),
        code: ErrorCode.FileInvalidType
      };
    } else if (
      currentResourceType === RESOURCE_TYPE.COLLECTION ||
      currentResourceType === RESOURCE_TYPE.FOLDER ||
      currentResourceType === RESOURCE_TYPE.NAMESPACE
    ) {
      return null;
    } else {
      return {
        message: t(T.filesPermission),
        code: ErrorCode.FileInvalidType
      };
    }
  }
  return null;
};

export const dropHandler = ({
  acceptedFiles,
  fileRejections,
  currentResourceType,
  dispatch,
  t,
  toggleUploadModal,
  unauthorized = false
}: DropHandler) => {
  if (unauthorized) {
    dispatch(
      setAlert({
        type: ALERT.ERROR,
        msg: t(T.unauthorizedFilesError)
      })
    );
    return;
  } else {
    if (
      UPLOAD_CONSTRAINT_RESOURCES.includes(currentResourceType) &&
      !fileRejections.length
    ) {
      const path = (acceptedFiles as IDropZoneFileValidator[])?.[0]?.path;
      if (
        (toggleUploadModal && path?.split('/')?.length >= 2) ||
        !UPLOAD_CONSTRAINT_RESOURCES.includes(currentResourceType)
      ) {
        toggleUploadModal?.();
      }
      return;
    }
    if (
      fileRejections.length &&
      !acceptedFiles.length &&
      !UPLOAD_CONSTRAINT_RESOURCES.includes(currentResourceType)
    ) {
      dispatch(
        setAlert({
          type: ALERT.ERROR,
          msg: t(T.dropDocsRestriction)
        })
      );
    } else {
      if (fileRejections.length && acceptedFiles.length) {
        dispatch(
          setAlert({
            type: ALERT.WARNING,
            msg: t(T.dropDocsPartialRestriction)
          })
        );
      }
      if (toggleUploadModal) {
        toggleUploadModal();
      }
    }
  }
};

export type UploadStatusMessageType = {
  message: string;
  type: AlertColor;
};

export const areFilesValid = (acceptedFiles, dispatch, t) => {
  let isValid = true;
  acceptedFiles.map((file) => {
    if (file.name.length > FILE_NAME_LENGTH_LIMIT) {
      dispatch(
        setAlert({
          msg: t(T.maxDocSize, {
            name: file.name.slice(0, 15) + '...'
          }),
          type: ALERT.ERROR
        })
      );
      isValid = false;
    }
  });

  return isValid;
};

export const StyledTabs = styled(Tabs)(({ theme }) => {
  return {
    marginTop: '6px',
    marginBottom: '12px'
  };
});

export const disallowedExtensions = ['.zip', '.exe', '.csv', '.mp4', '.pdf'];
export const MAX_UPLOAD_URL_LIMIT = 5;
export const restrictedEndPoints = ['login', 'signin', 'auth', 'captcha'];
export const isRestrictedUrl = (url: string): boolean => {
  if (!url) return true;

  try {
    const urlObj = new URL(url.trim());
    const hostname = urlObj.hostname.toLowerCase();
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return true;
    }
    const restrictedPatterns = ['captcha', 'recaptcha', 'cloudflare'];
    if (restrictedPatterns.some((pattern) => hostname.includes(pattern))) {
      return true;
    }

    return false;
  } catch {
    return true;
  }
};

// A browser reports no mime type at all for formats the OS does not register -
// .caf and .amr among them - so neither of these lists is sufficient on its own.
// The mime list matches what the browser reports; the extension list keeps such
// files selectable in the picker and is the fallback used by validation.
export const AUDIO_VIDEO_ACCEPT_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/webm',
  'audio/mp4',
  'audio/m4a',
  'audio/x-m4a',
  'audio/aac',
  'audio/x-aac',
  'audio/aacp',
  'audio/ogg',
  'audio/x-caf',
  'audio/caf',
  'audio/amr',
  'audio/x-amr',
  'audio/3gpp',
  'video/mp4',
  'video/webm',
  'video/mpeg',
  'video/quicktime',
  'video/x-quicktime',
  'video/x-m4v',
  'video/m4v',
  'video/3gpp'
];

export const AUDIO_VIDEO_ACCEPT_EXTENSIONS = [
  '.mp3',
  '.mp4',
  '.mpga',
  '.mpeg',
  '.wav',
  '.m4a',
  '.webm',
  '.weba',
  '.mov',
  '.m4v',
  '.3gp',
  '.aac',
  '.caf',
  '.amr'
];

export const getDropzoneAccept = (isAudioUpload: boolean = false) => {
  if (isAudioUpload) {
    return [...AUDIO_VIDEO_ACCEPT_MIME_TYPES, ...AUDIO_VIDEO_ACCEPT_EXTENSIONS];
  }

  return { ...FILE_FORMAT };
};

/**
 * Accepts a file when the browser-reported mime type is allowed, or - when the
 * browser reports nothing usable - when the filename extension is allowed.
 */
export const isAllowedAudioVideoFile = (file: File): boolean => {
  if (file.type && AUDIO_VIDEO_ACCEPT_MIME_TYPES.includes(file.type)) {
    return true;
  }
  const dot = file.name.lastIndexOf('.');
  if (dot < 0) {
    return false;
  }
  return AUDIO_VIDEO_ACCEPT_EXTENSIONS.includes(
    file.name.slice(dot).toLowerCase()
  );
};
